import React from 'react';
import clsx from 'clsx';
import { type TFunction } from 'i18next';
import { useDispatch } from 'react-redux';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import { Paper, styled } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { DataGridPremiumProps, GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useDeepCompareEffect } from '@fuse/hooks';

import { executeStored, getItemId } from 'app/main/components/Utils';
import { type MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';
import { type PaginationType } from 'app/main/components/hooks/usePagination';
import { type SelectionChangeEvent, type SelectionProps } from 'app/main/components/hooks/useSelection';
import useSettings from 'app/main/components/hooks/useSettings';
import useCancelablePromise from 'app/main/components/hooks/useCancelablePromise';
import ErrorAlert from 'app/main/components/ErrorAlert';

/* eslint-disable-next-line import/no-cycle */
import useViewTableState from '../components/useViewTableState';
import { LoadingOverlay, NoRowsOverlay, ViewTableToolbar, type ViewTableToolbarProps } from './ViewTableComponents';
import ViewFooter, { type ViewFooterCustomInfoProps, type ViewFooterProps } from '../components/ViewFooter';
import ViewTableColumnsVisibilityDialog from './ViewTableColumnsVisibilityDialog';

export type ViewTableColumnsLayoutType = (params: {
	settings: any;
	t: TFunction;
	updateRefresh: VoidFunction;
}) => GridColDef[];

export interface ViewTableProps {
	className?: string;
	id?: string;
	module: string;
	idField?: string;
	query?: {
		storedProcedure?: string;
		params?: any;
	};
	columns: GridColDef[];
	defaultFilters?: MiddlewhereFilter[];
	defaultQuery?: any;
	filters?: MiddlewhereFilter[];
	paginationProps?: PaginationType;
	refresh?: boolean;
	updateRefresh?: VoidFunction;
	defaultNRows?: number;
	multilineRows?: boolean;
	slots?: {
		toolbar?: (props: ViewTableToolbarProps) => React.ReactNode;
		footer?: (props: ViewFooterProps) => React.ReactNode;
		footerCustomInfo?: (props: ViewFooterCustomInfoProps) => React.ReactNode;
	};
	getRowClassName?: DataGridPremiumProps['getRowClassName'];
	getCellClassName?: DataGridPremiumProps['getCellClassName'];
	rowsParser?: (rows: any[]) => any;
	selectionProps?: SelectionProps;
	isRowSelectable?: DataGridPremiumProps['isRowSelectable'];
	showFilters?: boolean;
	showExportButton?: boolean;
	handleCellClick?: DataGridPremiumProps['onCellClick'];
}

declare module '@mui/x-data-grid-premium' {
	interface ToolbarPropsOverrides extends ViewTableToolbarProps {}
	interface FooterPropsOverrides extends ViewFooterProps {}
}

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	'.MuiDataGrid-virtualScroller': {
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	'.MuiDataGrid-cell': {
		display: 'flex',
		alignItems: 'center',
		fontVariantNumeric: 'tabular-nums'
	}
	// '.MuiDataGrid-row:last-child > .MuiDataGrid-cell': {
	// 	borderBottom: 0
	// }
}));

function ViewTable({ showFilters = true, showExportButton = true, defaultNRows = 30, ...props }: ViewTableProps) {
	const dispatch = useDispatch();
	const { settings } = useSettings(props.module);
	const getCancelablePromise = useCancelablePromise();
	const { resetBoundary, showBoundary } = useErrorBoundary();

	const [rows, setRows] = React.useState<any[]>([]);
	const [additionalInfo, setAdditionalInfo] = React.useState<any>({});

	const parsedRows = (props.rowsParser ? props.rowsParser(rows) : rows) || [];

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	const viewState = useViewTableState({
		id: props.id,
		module: props.module,
		filter: {
			defaultFilters: props.defaultFilters,
			defaultQuery: props.defaultQuery,
			filters: props.filters
		},
		paginator: { defaultNRows }
	});

	const paginationProps = props.paginationProps || viewState.paginationProps;

	const { filterProps, orderProps, paginatorProps } = paginationProps;
	const { nPage, nRows, maxRows, setMaxRows, isLoading, setIsLoading } = paginatorProps;

	const handleSortModelChange = React.useCallback(
		sortModel => {
			orderProps.setOrderBy(sortModel[0]?.field);
			orderProps.setOrderOperator(sortModel[0]?.sort);
		},
		[orderProps]
	);

	// const handleColumnVisibilityModelChange = (column, visible) =>
	// 	viewState.setColumnVisibilityModel({ [column]: visible });

	const [openColumnVisibilityDialog, setOpenColumnVisibilityDialog] = React.useState(false);

	const hasHiddenColumns = Object.values(viewState.columnVisibilityModel).includes(false);

	const getRowId = item => getItemId(item, props.idField, settings);

	const { mode, selectedItems } = props.selectionProps || {};
	const selectedItemsUids = new Set<GridRowId>(selectedItems?.map(getRowId) || []);

	const handleSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
		const event: SelectionChangeEvent = new CustomEvent('handleSelectionChange', {
			detail: { items: parsedRows, newSelectedItemsIds: [...rowSelectionModel.ids] }
		});
		document.dispatchEvent(event);
	};

	useDeepCompareEffect(() => {
		setIsLoading(true);
		executeStored({
			action:
				props.query?.storedProcedure || `${settings.modu_external ? 'ext_' : ''}st_${settings.modu_title}_list`,
			params: {
				n_page: nPage,
				n_rows: nRows,
				filters: filterProps?.filters,
				query: filterProps?.query,
				order_by: orderProps?.orderString,
				...(props.query?.params || {})
			},
			dispatch,
			getCancelablePromise,
			successHandler: response => {
				const { n_record, list, ...other } = response.data;
				setMaxRows(n_record);
				setRows(list || []);
				setAdditionalInfo(other);
				resetBoundary();
				setIsLoading(false);
			},
			errorHandler: ex => {
				showBoundary(ex);
				setIsLoading(false);
			}
		});
	}, [
		filterProps?.filters,
		filterProps?.query,
		nPage,
		nRows,
		orderProps?.orderBy,
		orderProps?.orderOperator,
		props.refresh,
		refresh,
		settings
	]);

	const footerCustomInfo = props.slots?.footerCustomInfo
		? props.slots?.footerCustomInfo({ items: parsedRows, selectedItems, additionalInfo })
		: undefined;

	return (
		<>
			<StyledDataGrid
				className="rounded-8 border-0"
				slots={{
					toolbar: props.slots?.toolbar || ViewTableToolbar,
					footer: props.slots?.footer || ViewFooter,
					loadingOverlay: LoadingOverlay,
					noRowsOverlay: NoRowsOverlay
				}}
				slotProps={{
					toolbar: {
						module: props.module,
						paginationProps,
						showFilters,
						showExportButton,
						showBorderBottom: true,
						updateRefresh: props.updateRefresh || updateRefresh,
						hasHiddenColumns,
						showColumnVisibilityDialog: () => setOpenColumnVisibilityDialog(true)
					},
					footer: {
						paginatorProps,
						customInfo: footerCustomInfo
					}
				}}
				columns={props.columns}
				rows={parsedRows}
				rowCount={maxRows}
				loading={isLoading}
				density="compact"
				showToolbar
				getRowId={getRowId}
				getRowClassName={props.getRowClassName}
				getRowHeight={() => (props.multilineRows ? 'auto' : undefined)}
				getCellClassName={props.getCellClassName}
				paginationModel={{ page: nPage, pageSize: nRows }}
				paginationMode="server"
				sortingMode="server"
				onSortModelChange={handleSortModelChange}
				disableVirtualization
				disableColumnMenu
				checkboxSelection={!!props.selectionProps}
				rowSelectionModel={{ type: 'include', ids: selectedItemsUids }}
				onRowSelectionModelChange={handleSelectionModelChange}
				isRowSelectable={props.isRowSelectable}
				keepNonExistentRowsSelected
				// disableRowSelectionOnClick={!props.handleCellClick}
				disableMultipleRowSelection={mode === 'single'}
				disableRowSelectionExcludeModel
				disableRowSelectionOnClick
				// disableRowSelectionOnClick={!props.handleCellClick}
				onCellClick={props.handleCellClick}
				columnVisibilityModel={viewState.columnVisibilityModel}
			/>
			<ViewTableColumnsVisibilityDialog
				open={openColumnVisibilityDialog}
				closeDialog={() => setOpenColumnVisibilityDialog(false)}
				columns={props.columns}
				columnVisibilityModel={viewState.columnVisibilityModel}
				setColumnVisibilityModel={viewState.setColumnVisibilityModel}
			/>
		</>
	);
}

export default ({ className, ...props }: ViewTableProps) => (
	<Paper className={clsx('flex w-full flex-col rounded-8', className)} variant="outlined">
		<ErrorBoundary fallbackRender={({ error }) => <ErrorAlert className="rounded-8" error={error} />}>
			<ViewTable {...props} />
		</ErrorBoundary>
	</Paper>
);
